import { FEATURE_FLAGS, REDIS_FEATURE_FLAGS } from 'dashboard/featureFlags';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { hasPermissions } from 'dashboard/helper/permissionsHelper';
import {
  INBOX_TYPES,
  isClaudiaChannelType,
} from 'shared/mixins/inboxMixin';

export const JOURNEYS = {
  CHANNEL_SETUP: 'channel_setup',
  AI_SETUP: 'ai_setup',
  TEAM_OPERATIONS: 'team_operations',
  INDICATORS: 'indicators',
};

export const STAGES = {
  ESSENTIAL: 'essential',
  RECOMMENDED: 'recommended',
  ADVANCED: 'advanced',
};

export const JOURNEY_ORDER = [
  JOURNEYS.CHANNEL_SETUP,
  JOURNEYS.AI_SETUP,
  JOURNEYS.TEAM_OPERATIONS,
  JOURNEYS.INDICATORS,
];

export const STAGE_ORDER = [
  STAGES.ESSENTIAL,
  STAGES.RECOMMENDED,
  STAGES.ADVANCED,
];

export const CHANNELS = {
  WEB: INBOX_TYPES.WEB,
  WHATSAPP: INBOX_TYPES.WHATSAPP,
  EMAIL: INBOX_TYPES.EMAIL,
};

export const TARGET_TYPES = {
  ACCOUNT: 'account',
  INBOX_TAB: 'inbox_tab',
  NONE: 'none',
};

export const MANUAL_COMPLETION_PERMISSIONS = ['administrator', 'cx_engineer'];

const ADMIN = ['administrator'];
const ADMIN_AND_AGENT = ['administrator', 'agent'];
const CLAUDIA_EDITORS = ['administrator', 'cx_engineer'];
const REPORT_VIEWERS = ['administrator', 'report_manage'];

const CC_ARTICLES = 'https://cloudchat.cloudhumans.com/hc/cloud-chat/articles';
const CLAUDIA_ARTICLES =
  'https://cloudchat.cloudhumans.com/hc/claudia/articles';
const FLOWS_ARTICLES =
  'https://cloudchat.cloudhumans.com/hc/fluxos-controlados-e-tools/articles';

const accountTarget = path => ({ type: TARGET_TYPES.ACCOUNT, path });
const inboxTabTarget = (tab, channelType = null) => ({
  type: TARGET_TYPES.INBOX_TAB,
  tab,
  channelType,
});
const claudiaSettingsTarget = section =>
  accountTarget(`claudia/settings#${section}`);

export const ONBOARDING_STEPS = [
  {
    id: 'create_users',
    journey: JOURNEYS.CHANNEL_SETUP,
    stage: STAGES.ESSENTIAL,
    requiredForGoLive: true,
    permissions: ADMIN,
    featureFlag: FEATURE_FLAGS.AGENT_MANAGEMENT,
    target: accountTarget('settings/agents/list'),
    faqUrl: `${CC_ARTICLES}/1737036577-como-criar-um-novo-usuario-no-cloud-chat`,
  },
  {
    id: 'create_inbox',
    journey: JOURNEYS.CHANNEL_SETUP,
    stage: STAGES.ESSENTIAL,
    requiredForGoLive: true,
    permissions: ADMIN,
    featureFlag: FEATURE_FLAGS.INBOX_MANAGEMENT,
    target: accountTarget('settings/inboxes/list'),
    faqUrl: `${CC_ARTICLES}/1751655498-como-instalar-o-web_chat-no-site-e-passar-dados-do-contato-automaticamente`,
  },
  {
    id: 'configure_inbox',
    journey: JOURNEYS.CHANNEL_SETUP,
    stage: STAGES.ESSENTIAL,
    requiredForGoLive: true,
    permissions: ADMIN,
    featureFlag: FEATURE_FLAGS.INBOX_MANAGEMENT,
    target: inboxTabTarget('inbox_settings'),
    faqUrl: `${CC_ARTICLES}/1743448602-configuracao-de-limite-de-tickets-conversas-concomitantes-em-paralelo-por-agente-e-atribuicao-automatica-via-round-robin`,
  },
  {
    id: 'claudia_create_project',
    showWhenClaudiaOnly: true,
    journey: JOURNEYS.AI_SETUP,
    stage: STAGES.ESSENTIAL,
    requiredForGoLive: true,
    permissions: CLAUDIA_EDITORS,
    runByCloudHumans: true,
    target: { type: TARGET_TYPES.NONE },
    faqUrl: `${CLAUDIA_ARTICLES}/1759939755-configuracoes-iniciais-e-essenciais-para-realizar-na-claudia`,
  },
  {
    id: 'claudia_training',
    showWhenClaudiaOnly: true,
    journey: JOURNEYS.AI_SETUP,
    stage: STAGES.ESSENTIAL,
    permissions: CLAUDIA_EDITORS,
    target: { type: TARGET_TYPES.NONE },
    faqUrl: `${CLAUDIA_ARTICLES}/1740238717-principais-telas-do-hub`,
  },
  {
    id: 'claudia_general_settings',
    showWhenClaudiaOnly: true,
    journey: JOURNEYS.AI_SETUP,
    stage: STAGES.ESSENTIAL,
    requiredForGoLive: true,
    permissions: CLAUDIA_EDITORS,
    featureFlag: REDIS_FEATURE_FLAGS.CLAUDIA_SETTINGS,
    target: claudiaSettingsTarget('general'),
    faqUrl: `${CLAUDIA_ARTICLES}/1782822893-configuracoes-da-claud_ia-geral`,
  },
  {
    id: 'claudia_behavior',
    showWhenClaudiaOnly: true,
    journey: JOURNEYS.AI_SETUP,
    stage: STAGES.ESSENTIAL,
    requiredForGoLive: true,
    permissions: CLAUDIA_EDITORS,
    featureFlag: REDIS_FEATURE_FLAGS.CLAUDIA_SETTINGS,
    target: claudiaSettingsTarget('behavior'),
    faqUrl: `${CLAUDIA_ARTICLES}/1782822905-configuracoes-da-claud_ia-comportamento`,
  },
  {
    id: 'claudia_handover',
    showWhenClaudiaOnly: true,
    journey: JOURNEYS.AI_SETUP,
    stage: STAGES.ESSENTIAL,
    requiredForGoLive: true,
    permissions: CLAUDIA_EDITORS,
    featureFlag: REDIS_FEATURE_FLAGS.CLAUDIA_SETTINGS,
    target: claudiaSettingsTarget('handover'),
    faqUrl: `${CLAUDIA_ARTICLES}/1782822916-configuracoes-da-claud_ia-transferencia`,
  },
  {
    id: 'claudia_knowledge_base',
    showWhenClaudiaOnly: true,
    journey: JOURNEYS.AI_SETUP,
    stage: STAGES.ESSENTIAL,
    requiredForGoLive: true,
    permissions: CLAUDIA_EDITORS,
    target: accountTarget('claudia/knowledge-bases'),
    faqUrl: `${CLAUDIA_ARTICLES}/1753201683-validacao-de-conteudo`,
  },
  {
    id: 'claudia_contents',
    showWhenClaudiaOnly: true,
    journey: JOURNEYS.AI_SETUP,
    stage: STAGES.ESSENTIAL,
    requiredForGoLive: true,
    permissions: CLAUDIA_EDITORS,
    target: accountTarget('claudia/knowledge-bases'),
    faqUrl: `${CLAUDIA_ARTICLES}/1752186948-usando-a-tela-de-conteudos`,
  },
  {
    id: 'claudia_manual_content',
    showWhenClaudiaOnly: true,
    journey: JOURNEYS.AI_SETUP,
    stage: STAGES.RECOMMENDED,
    permissions: CLAUDIA_EDITORS,
    target: accountTarget('claudia/knowledge-bases'),
    faqUrl: `${CLAUDIA_ARTICLES}/1740240102-boas-praticas-na-criacao-e-manutencao-de-conteudos`,
  },
  {
    id: 'claudia_helpdesk_integration',
    journey: JOURNEYS.AI_SETUP,
    stage: STAGES.ESSENTIAL,
    requiredForGoLive: true,
    permissions: CLAUDIA_EDITORS,
    runByCloudHumans: true,
    onlyWhenClaudiaOnly: true,
    target: { type: TARGET_TYPES.NONE },
  },
  {
    id: 'connect_claudia_inbox',
    journey: JOURNEYS.AI_SETUP,
    stage: STAGES.ESSENTIAL,
    requiredForGoLive: true,
    permissions: ADMIN,
    featureFlag: FEATURE_FLAGS.INBOX_MANAGEMENT,
    target: inboxTabTarget('claudia'),
    faqUrl: `${CLAUDIA_ARTICLES}/1740227808-cloud-chat-guia-passo-a-passo-da-integracao`,
  },
  {
    id: 'activate_production',
    journey: JOURNEYS.AI_SETUP,
    stage: STAGES.ESSENTIAL,
    requiredForGoLive: true,
    permissions: ADMIN,
    featureFlag: FEATURE_FLAGS.INBOX_MANAGEMENT,
    target: inboxTabTarget('claudia'),
    faqUrl: `${CLAUDIA_ARTICLES}/1748357529-como`,
  },
  {
    id: 'watch_training',
    journey: JOURNEYS.CHANNEL_SETUP,
    stage: STAGES.ESSENTIAL,
    permissions: ADMIN_AND_AGENT,
    target: { type: TARGET_TYPES.NONE },
    faqUrl: `${CC_ARTICLES}/1775602790-treinamento-completo-de-cloud-chat-introducao-e-configuracao-da-plataforma`,
  },
  {
    id: 'whatsapp_meta_prerequisites',
    channel: CHANNELS.WHATSAPP,
    journey: JOURNEYS.CHANNEL_SETUP,
    stage: STAGES.ESSENTIAL,
    requiredForGoLive: true,
    permissions: ADMIN,
    featureFlag: FEATURE_FLAGS.INBOX_MANAGEMENT,
    target: { type: TARGET_TYPES.NONE },
    faqUrl: `${CC_ARTICLES}/1750242925-configurando-whats_app-com-coexistencia-via-cadastro-incorporado-no-cloud-chat`,
  },
  {
    id: 'email_forwarding',
    channel: CHANNELS.EMAIL,
    journey: JOURNEYS.CHANNEL_SETUP,
    stage: STAGES.ESSENTIAL,
    requiredForGoLive: true,
    permissions: ADMIN,
    featureFlag: FEATURE_FLAGS.INBOX_MANAGEMENT,
    target: inboxTabTarget('configuration', CHANNELS.EMAIL),
  },
  {
    id: 'account_general_settings',
    journey: JOURNEYS.CHANNEL_SETUP,
    stage: STAGES.RECOMMENDED,
    permissions: ADMIN,
    target: accountTarget('settings/general'),
    faqUrl: `${CC_ARTICLES}/1737930836-configuracoes-da-conta-configuracoes-basicas-de-nome-e-idioma-do-espaco-alem-de-tempo-de-fechamento-automatico`,
  },
  {
    id: 'business_hours',
    journey: JOURNEYS.CHANNEL_SETUP,
    stage: STAGES.RECOMMENDED,
    permissions: ADMIN,
    featureFlag: FEATURE_FLAGS.INBOX_MANAGEMENT,
    target: inboxTabTarget('businesshours'),
    faqUrl: `${CC_ARTICLES}/1775175068-como-configurar-mensagem-de-ausencia-automatica`,
  },
  {
    id: 'claudia_lifecycle',
    showWhenClaudiaOnly: true,
    journey: JOURNEYS.AI_SETUP,
    stage: STAGES.RECOMMENDED,
    permissions: CLAUDIA_EDITORS,
    featureFlag: REDIS_FEATURE_FLAGS.CLAUDIA_SETTINGS,
    target: claudiaSettingsTarget('lifecycle'),
    faqUrl: `${CLAUDIA_ARTICLES}/1782822931-configuracoes-da-claud_ia-ciclo-de-vida`,
  },
  {
    id: 'claudia_media',
    showWhenClaudiaOnly: true,
    journey: JOURNEYS.AI_SETUP,
    stage: STAGES.RECOMMENDED,
    permissions: CLAUDIA_EDITORS,
    featureFlag: REDIS_FEATURE_FLAGS.CLAUDIA_SETTINGS,
    target: claudiaSettingsTarget('media'),
    faqUrl: `${CLAUDIA_ARTICLES}/1782822934-configuracoes-da-claud_ia-midia`,
  },
  {
    id: 'claudia_playground',
    showWhenClaudiaOnly: true,
    journey: JOURNEYS.AI_SETUP,
    stage: STAGES.RECOMMENDED,
    permissions: CLAUDIA_EDITORS,
    target: { type: TARGET_TYPES.NONE },
    faqUrl: `${CLAUDIA_ARTICLES}/1781542911-como-testar-a-claud_ia-no-playground-antes-de-publicar`,
  },
  {
    id: 'claudia_audit',
    showWhenClaudiaOnly: true,
    journey: JOURNEYS.AI_SETUP,
    stage: STAGES.RECOMMENDED,
    permissions: CLAUDIA_EDITORS,
    target: { type: TARGET_TYPES.NONE },
    faqUrl: `${CLAUDIA_ARTICLES}/1744121409-auditoria-de-tickets`,
  },
  {
    id: 'install_widget',
    channel: CHANNELS.WEB,
    journey: JOURNEYS.CHANNEL_SETUP,
    stage: STAGES.ESSENTIAL,
    permissions: ADMIN,
    featureFlag: FEATURE_FLAGS.INBOX_MANAGEMENT,
    target: inboxTabTarget('widgetBuilder', INBOX_TYPES.WEB),
    faqUrl: `${CC_ARTICLES}/1751655498-como-instalar-o-web_chat-no-site-e-passar-dados-do-contato-automaticamente`,
  },
  {
    id: 'whatsapp_templates',
    channel: CHANNELS.WHATSAPP,
    journey: JOURNEYS.CHANNEL_SETUP,
    stage: STAGES.RECOMMENDED,
    permissions: ADMIN,
    featureFlag: FEATURE_FLAGS.INBOX_MANAGEMENT,
    target: inboxTabTarget('whatsappTemplates', INBOX_TYPES.WHATSAPP),
    faqUrl: `${CC_ARTICLES}/1754505671-gestao-de-templates-whats_app`,
  },
  {
    id: 'canned_responses',
    journey: JOURNEYS.TEAM_OPERATIONS,
    stage: STAGES.RECOMMENDED,
    permissions: ADMIN_AND_AGENT,
    featureFlag: FEATURE_FLAGS.CANNED_RESPONSES,
    target: accountTarget('settings/canned-response/list'),
    faqUrl: `${CC_ARTICLES}/1737738904-configuracao-de-atalhos-do-cloud-chat`,
  },
  {
    id: 'pre_chat_form',
    channel: CHANNELS.WEB,
    journey: JOURNEYS.CHANNEL_SETUP,
    stage: STAGES.ADVANCED,
    permissions: ADMIN,
    featureFlag: FEATURE_FLAGS.INBOX_MANAGEMENT,
    target: inboxTabTarget('preChatForm', INBOX_TYPES.WEB),
  },
  {
    id: 'create_teams',
    journey: JOURNEYS.TEAM_OPERATIONS,
    stage: STAGES.ESSENTIAL,
    permissions: ADMIN,
    featureFlag: FEATURE_FLAGS.TEAM_MANAGEMENT,
    target: accountTarget('settings/teams/list'),
    faqUrl: `${CC_ARTICLES}/1737931730-como-criar-e-gerenciar-times-e-departamentos`,
  },
  {
    id: 'inbox_collaborators',
    journey: JOURNEYS.CHANNEL_SETUP,
    stage: STAGES.RECOMMENDED,
    permissions: ADMIN,
    featureFlag: FEATURE_FLAGS.INBOX_MANAGEMENT,
    target: inboxTabTarget('collaborators'),
    faqUrl: `${CC_ARTICLES}/1738151515-por-que-um-usuario-nao-ve-uma-inbox-especifica-como-resolver-problemas-de-visibilidade-de-caixas-para-usuarios`,
  },
  {
    id: 'custom_availability',
    journey: JOURNEYS.TEAM_OPERATIONS,
    stage: STAGES.RECOMMENDED,
    permissions: ADMIN,
    target: accountTarget('settings/custom-availability/list'),
    faqUrl: `${CC_ARTICLES}/1742826798-disponibilidades-customizadas-para-usuario`,
  },
  {
    id: 'macros',
    journey: JOURNEYS.TEAM_OPERATIONS,
    stage: STAGES.RECOMMENDED,
    permissions: ADMIN,
    featureFlag: FEATURE_FLAGS.MACROS,
    target: accountTarget('settings/macros'),
    faqUrl: `${CC_ARTICLES}/1774910110-macros-com-atributos-customizados-e-rastreabilidade-de-mudancas`,
  },
  {
    id: 'automations',
    journey: JOURNEYS.TEAM_OPERATIONS,
    stage: STAGES.RECOMMENDED,
    permissions: ADMIN,
    featureFlag: FEATURE_FLAGS.AUTOMATIONS,
    target: accountTarget('settings/automation/list'),
    faqUrl: `${CC_ARTICLES}/1744401344-gerenciando-automacoes-no-cloud_chat`,
  },
  {
    id: 'auto_resolve',
    journey: JOURNEYS.TEAM_OPERATIONS,
    stage: STAGES.RECOMMENDED,
    permissions: ADMIN,
    featureFlag: FEATURE_FLAGS.AUTO_RESOLVE_CONVERSATIONS,
    target: accountTarget('settings/general'),
    faqUrl: `${CC_ARTICLES}/1744800410-resolucao-automatica-de-tickets`,
  },
  {
    id: 'import_contacts',
    journey: JOURNEYS.TEAM_OPERATIONS,
    stage: STAGES.ADVANCED,
    permissions: ADMIN,
    featureFlag: FEATURE_FLAGS.CRM,
    target: accountTarget('contacts'),
    faqUrl: `${CC_ARTICLES}/1749683256-importar-contatos-em-massa-via-csv`,
  },
  {
    id: 'conversation_filters',
    journey: JOURNEYS.TEAM_OPERATIONS,
    stage: STAGES.RECOMMENDED,
    permissions: ADMIN_AND_AGENT,
    target: accountTarget('dashboard'),
    faqUrl: `${CC_ARTICLES}/1775688043-filtros-de-conversas`,
  },
  {
    id: 'help_center',
    journey: JOURNEYS.TEAM_OPERATIONS,
    stage: STAGES.RECOMMENDED,
    permissions: ADMIN,
    featureFlag: FEATURE_FLAGS.HELP_CENTER,
    target: accountTarget('portals'),
  },
  {
    id: 'campaigns',
    journey: JOURNEYS.TEAM_OPERATIONS,
    stage: STAGES.ADVANCED,
    permissions: ADMIN,
    featureFlag: FEATURE_FLAGS.CAMPAIGNS,
    target: accountTarget('campaigns/one_off'),
    faqUrl: `${CC_ARTICLES}/1737728471-criando-campanha-proativa-no-whats_app-guia-passo-a-passo`,
  },
  {
    id: 'controlled_flows',
    showWhenClaudiaOnly: true,
    journey: JOURNEYS.AI_SETUP,
    stage: STAGES.ADVANCED,
    permissions: CLAUDIA_EDITORS,
    target: accountTarget('claudia/controlled-flows'),
    faqUrl: `${FLOWS_ARTICLES}/1764699368-o-que-e-o-eddie`,
  },
  {
    id: 'claudia_agents',
    showWhenClaudiaOnly: true,
    journey: JOURNEYS.AI_SETUP,
    stage: STAGES.ADVANCED,
    permissions: CLAUDIA_EDITORS,
    featureFlag: REDIS_FEATURE_FLAGS.CLAUDIA_AGENTS_V2,
    target: accountTarget('claudia/agents'),
    faqUrl: `${FLOWS_ARTICLES}/1764699800-passo-a-passo-para-lancar-um-novo-eddie`,
  },
  {
    id: 'claudia_tools',
    showWhenClaudiaOnly: true,
    journey: JOURNEYS.AI_SETUP,
    stage: STAGES.ADVANCED,
    permissions: CLAUDIA_EDITORS,
    featureFlag: REDIS_FEATURE_FLAGS.CLAUDIA_AGENTS_V2,
    target: accountTarget('claudia/tools'),
    faqUrl: `${FLOWS_ARTICLES}/1782326368-eddie-fluxos-controlados-e-tools-o-que-mudou`,
  },
  {
    id: 'claudia_mcp',
    showWhenClaudiaOnly: true,
    journey: JOURNEYS.AI_SETUP,
    stage: STAGES.ADVANCED,
    permissions: CLAUDIA_EDITORS,
    featureFlag: REDIS_FEATURE_FLAGS.CLAUDIA_AGENTS_V2,
    target: accountTarget('claudia/mcp'),
    faqUrl: `${CLAUDIA_ARTICLES}/1774967148-como-configurar-servidores-mcp-para-seus-agentes`,
  },
  {
    id: 'create_labels',
    showWhenClaudiaOnly: true,
    journey: JOURNEYS.INDICATORS,
    stage: STAGES.ESSENTIAL,
    permissions: ADMIN,
    featureFlag: FEATURE_FLAGS.LABELS,
    target: accountTarget('settings/labels/list'),
    faqUrl: `${CC_ARTICLES}/1776170906-funciona`,
  },
  {
    id: 'claudia_tags',
    showWhenClaudiaOnly: true,
    journey: JOURNEYS.AI_SETUP,
    stage: STAGES.RECOMMENDED,
    permissions: CLAUDIA_EDITORS,
    featureFlag: REDIS_FEATURE_FLAGS.CLAUDIA_SETTINGS,
    target: claudiaSettingsTarget('tags'),
    faqUrl: `${CLAUDIA_ARTICLES}/1782822940-configuracoes-da-claud_ia-tags`,
  },
  {
    id: 'native_attributes',
    journey: JOURNEYS.INDICATORS,
    stage: STAGES.RECOMMENDED,
    permissions: ADMIN,
    target: accountTarget('settings/native-attributes/list'),
    faqUrl: `${CC_ARTICLES}/1741632825-campos-obrigatorios-para-fechamento-de-tickets`,
  },
  {
    id: 'custom_attributes',
    journey: JOURNEYS.INDICATORS,
    stage: STAGES.RECOMMENDED,
    permissions: ADMIN,
    featureFlag: FEATURE_FLAGS.CUSTOM_ATTRIBUTES,
    target: accountTarget('settings/custom-attributes/list'),
    faqUrl: `${CC_ARTICLES}/1737394690-criando-campos-customizados-de-contatos-e-conversas`,
  },
  {
    id: 'sla',
    journey: JOURNEYS.INDICATORS,
    stage: STAGES.RECOMMENDED,
    permissions: ADMIN,
    newFeatureFlag: 'sla_enabled',
    target: accountTarget('settings/sla/list'),
    faqUrl: `${CC_ARTICLES}/1768909115-sla-metricas-de-tempo`,
  },
  {
    id: 'csat_survey',
    journey: JOURNEYS.INDICATORS,
    stage: STAGES.RECOMMENDED,
    permissions: ADMIN,
    featureFlag: FEATURE_FLAGS.INBOX_MANAGEMENT,
    target: inboxTabTarget('inbox_settings'),
    faqUrl: `${CC_ARTICLES}/1738001549-entendendo-o-csat-como-configurar-por-canal-e-como-ver-os-resultados`,
  },
  {
    id: 'reports',
    journey: JOURNEYS.INDICATORS,
    stage: STAGES.RECOMMENDED,
    permissions: REPORT_VIEWERS,
    featureFlag: FEATURE_FLAGS.REPORTS,
    target: accountTarget('reports/overview'),
    faqUrl: `${CC_ARTICLES}/1750171277-visao-geral-dos-relatorios-padroes-do-cloud-chat`,
  },
  {
    id: 'report_builder',
    journey: JOURNEYS.INDICATORS,
    stage: STAGES.ADVANCED,
    permissions: REPORT_VIEWERS,
    featureFlag: FEATURE_FLAGS.REPORTS,
    target: accountTarget('reports/create-mode'),
    faqUrl: `${CC_ARTICLES}/1775678459-guia-basico-novo-construtor-de-relatorios`,
  },
  {
    id: 'improvement_center',
    showWhenClaudiaOnly: true,
    journey: JOURNEYS.AI_SETUP,
    stage: STAGES.ADVANCED,
    permissions: CLAUDIA_EDITORS,
    newFeatureFlag: 'enable_improvement_center',
    target: accountTarget('claudia/improvement-center'),
    faqUrl: `${CLAUDIA_ARTICLES}/1783537952-centro-de-melhorias-como-revisar-e-aprovar-as-sugestoes-de-conteudo-da-claud_ia`,
  },
];

const isVisibleForAccountType = (step, { isClaudiaOnly }) => {
  if (step.onlyWhenClaudiaOnly) return isClaudiaOnly;
  if (!isClaudiaOnly) return true;
  return !!step.showWhenClaudiaOnly;
};

const isVisibleForFlags = (
  step,
  { isFeatureEnabled, isNewFeatureFlagEnabled }
) => {
  if (step.featureFlag && !isFeatureEnabled(step.featureFlag)) return false;
  if (step.newFeatureFlag && !isNewFeatureFlagEnabled(step.newFeatureFlag)) {
    return false;
  }
  return true;
};

const matchesChannelType = (inbox, channelType) => {
  if (inbox.channel_type === channelType) return true;
  return (
    channelType === INBOX_TYPES.WHATSAPP &&
    inbox.channel_type === INBOX_TYPES.TWILIO &&
    inbox.medium === 'whatsapp'
  );
};

const resolveInboxTabUrl = (accountId, step, inboxes) => {
  const candidates = step.target.channelType
    ? inboxes.filter(inbox =>
        matchesChannelType(inbox, step.target.channelType)
      )
    : inboxes.filter(inbox => !isClaudiaChannelType(inbox.channel_type));

  if (candidates.length !== 1) {
    return frontendURL(`accounts/${accountId}/settings/inboxes/list`);
  }

  return frontendURL(
    `accounts/${accountId}/settings/inboxes/${candidates[0].id}?tab=${step.target.tab}`
  );
};

export const resolveStepUrl = (accountId, step, inboxes = []) => {
  const { target } = step;
  if (!target || target.type === TARGET_TYPES.NONE) return '';
  if (target.type === TARGET_TYPES.INBOX_TAB) {
    return resolveInboxTabUrl(accountId, step, inboxes);
  }
  return frontendURL(`accounts/${accountId}/${target.path}`);
};

export const buildOnboardingJourneys = ({
  accountId,
  userPermissions = [],
  isClaudiaOnly = false,
  inboxes = [],
  isFeatureEnabled = () => true,
  isNewFeatureFlagEnabled = () => false,
  completedStepIds = [],
  trackedStepIds = [],
  manualCompletions = [],
}) => {
  const completed = new Set(completedStepIds);
  const tracked = new Set(trackedStepIds);
  const manual = new Map(
    manualCompletions.map(completion => [completion.step_id, completion])
  );
  const flagOptions = { isFeatureEnabled, isNewFeatureFlagEnabled };

  const visibleSteps = ONBOARDING_STEPS.filter(step => {
    if (!hasPermissions(step.permissions, userPermissions)) return false;
    if (!isVisibleForAccountType(step, { isClaudiaOnly })) return false;
    return isVisibleForFlags(step, flagOptions);
  }).map(step => {
    const isTracked = tracked.has(step.id);
    const manualCompletion = isTracked ? undefined : manual.get(step.id);

    return {
      ...step,
      isTracked,
      isManual: !isTracked,
      isCompleted: isTracked
        ? completed.has(step.id)
        : Boolean(manualCompletion),
      completedBy: manualCompletion?.completed_by || '',
      completedAt: manualCompletion?.completed_at || null,
      configUrl: resolveStepUrl(accountId, step, inboxes),
    };
  });

  return JOURNEY_ORDER.map(journeyId => {
    const journeySteps = visibleSteps.filter(
      step => step.journey === journeyId
    );
    const stages = STAGE_ORDER.map(stageId => ({
      id: stageId,
      steps: journeySteps.filter(step => step.stage === stageId),
    })).filter(stage => stage.steps.length > 0);

    return {
      id: journeyId,
      stages,
      totalCount: journeySteps.length,
      completedCount: journeySteps.filter(step => step.isCompleted).length,
      hasPendingGoLiveStep: journeySteps.some(
        step => step.requiredForGoLive && !step.isCompleted
      ),
    };
  }).filter(journey => journey.totalCount > 0);
};

export const summarizeProgress = journeys => {
  const steps = journeys.flatMap(journey =>
    journey.stages.flatMap(stage => stage.steps)
  );
  const goLiveSteps = steps.filter(step => step.requiredForGoLive);
  const completedCount = steps.filter(step => step.isCompleted).length;

  return {
    totalCount: steps.length,
    completedCount,
    percentage: steps.length
      ? Math.round((completedCount / steps.length) * 100)
      : 0,
    goLiveTotalCount: goLiveSteps.length,
    goLiveCompletedCount: goLiveSteps.filter(step => step.isCompleted).length,
  };
};
